import { ArrowLeftOutlined, BellOutlined, LinkOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Descriptions, Empty, Skeleton, Space, Tag, Typography } from 'antd';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { apiCall } from '../../API/RestApi';

const { Title, Text, Paragraph } = Typography;

const typeColor = (type) => {
  const key = String(type || '').toLowerCase();
  if (key === 'approved') return 'green';
  if (key === 'rejected') return 'red';
  if (key === 'request') return 'blue';
  if (key === 'warning') return 'orange';
  if (key === 'payroll') return 'purple';
  if (key === 'overtime') return 'cyan';
  if (key === 'leave') return 'geekblue';
  if (key === 'attendance') return 'lime';
  return 'default';
};

function NotificationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.token);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const res = await apiCall({
        method: 'GET',
        url: `/app-notifications/${id}`,
        data: { headers: { Authorization: `Bearer ${token}` } },
      });
      if (!active) return;
      if (res?.status === 'success') {
        setData(res.data || null);
        setError('');
      } else {
        setData(null);
        setError(res?.message || 'Notifikasi tidak ditemukan.');
      }
      setLoading(false);
    };
    if (id && token) load();
    return () => { active = false; };
  }, [id, token]);

  const metadata = useMemo(() => {
    if (!data?.metadata) return null;
    if (typeof data.metadata === 'object') return data.metadata;
    try { return JSON.parse(data.metadata); } catch (_) { return null; }
  }, [data]);

  if (loading) {
    return <Card><Skeleton active paragraph={{ rows: 7 }} /></Card>;
  }

  if (error || !data) {
    return <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Kembali</Button>
        <Alert type="warning" showIcon message={error || 'Notifikasi tidak ditemukan.'} />
      </Space>
    </Card>;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', width: '100%' }}>
      <Card>
        <Space direction="vertical" size={18} style={{ width: '100%' }}>
          <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Kembali</Button>
            <Tag color={typeColor(data.event_type)}>{String(data.event_type || 'INFO').toUpperCase()}</Tag>
          </Space>

          <div>
            <Space align="start">
              <BellOutlined style={{ fontSize: 22, marginTop: 6 }} />
              <div>
                <Title level={3} style={{ marginBottom: 4 }}>{data.title || 'Notifikasi'}</Title>
                <Text type="secondary">{data.created_at ? moment(data.created_at).format('DD MMMM YYYY, HH:mm') : '-'}</Text>
              </div>
            </Space>
          </div>

          <Paragraph style={{ fontSize: 16, whiteSpace: 'pre-wrap', marginBottom: 0 }}>{data.message || '-'}</Paragraph>

          <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
            <Descriptions.Item label="Jenis">{data.event_type || '-'}</Descriptions.Item>
            <Descriptions.Item label="Sumber">{data.source_type || '-'}</Descriptions.Item>
            <Descriptions.Item label="Status">{data.read_at ? 'Sudah dibaca' : 'Belum dibaca'}</Descriptions.Item>
            <Descriptions.Item label="Tanggal">{data.created_at ? moment(data.created_at).format('DD/MM/YYYY HH:mm') : '-'}</Descriptions.Item>
          </Descriptions>

          {metadata && Object.keys(metadata).length > 0 && (
            <Descriptions title="Informasi Tambahan" column={1} bordered size="small">
              {Object.entries(metadata).map(([key, value]) => (
                <Descriptions.Item key={key} label={key.replace(/_/g, ' ')}>
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </Descriptions.Item>
              ))}
            </Descriptions>
          )}

          <Space wrap>
            {data.action_url && data.action_url !== '/notification' && (
              <Button type="primary" icon={<LinkOutlined />} onClick={() => navigate(data.action_url)}>
                Buka Halaman Terkait
              </Button>
            )}
            {!data.action_url && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Tidak ada halaman terkait" />}
          </Space>
        </Space>
      </Card>
    </div>
  );
}

export default NotificationDetail;
